import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';

export function McpLoginPage() {
  const { user, loading, firebaseUser, signInWithGoogle, signInWithEmail, createAccountWithEmail, error: authError } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const [bridging, setBridging] = useState(false);
  const bridgingRef = useRef(false);

  const loginRequestId = searchParams.get('login_request_id') || '';
  const state = searchParams.get('state') || '';

  const missingParams = !loginRequestId || !state;

  useEffect(() => {
    if (!user || !firebaseUser || bridgingRef.current || missingParams) return;
    bridgingRef.current = true;
    setBridging(true);
    setBridgeError(null);

    async function bridgeToScalekit() {
      try {
        const resp = await api.post<{ redirect_url: string }>('/v1/auth/mcp-bridge', {
          login_request_id: loginRequestId,
          state,
        });
        window.location.href = resp.redirect_url;
      } catch (err) {
        setBridgeError(err instanceof Error ? err.message : 'Failed to complete MCP authorization');
        setBridging(false);
        bridgingRef.current = false;
      }
    }

    bridgeToScalekit();
  }, [user, firebaseUser, loginRequestId, state, missingParams]);

  if (missingParams) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-2xl font-bold text-foreground mb-4">Invalid Request</h1>
          <p className="font-body text-muted-foreground">
            This page is used by MCP clients to authenticate. It requires valid authorization parameters.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (bridging) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-6"></div>
        <p className="font-body text-muted-foreground">Completing authorization...</p>
      </div>
    );
  }

  const displayError = bridgeError || authError;

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch {
      // error is set in AuthContext
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSigningIn(true);
    try {
      if (isCreatingAccount) {
        await createAccountWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch {
      // error is set in AuthContext
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-4">
            Authorize
          </p>
          <h1 className="font-display text-5xl md:text-6xl font-bold lowercase tracking-tight text-foreground mb-4">
            noctua
          </h1>
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            Sign in to connect your AI assistant
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm p-8 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <h2 className="font-display text-2xl font-semibold text-foreground mb-6 text-center">
            {isCreatingAccount ? 'Create an account' : 'Sign in to authorize'}
          </h2>

          {displayError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
              <p className="font-body text-sm text-destructive">{displayError}</p>
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
            <div>
              <label htmlFor="email" className="block font-body text-sm font-medium text-foreground mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-shadow"
                placeholder="you@example.com"
                disabled={isSigningIn}
              />
            </div>
            <div>
              <label htmlFor="password" className="block font-body text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-shadow"
                placeholder="••••••••"
                disabled={isSigningIn}
              />
            </div>
            <button
              type="submit"
              disabled={isSigningIn}
              className="w-full px-6 py-3 bg-foreground text-background border border-foreground rounded-sm font-body font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSigningIn
                ? (isCreatingAccount ? 'Creating account...' : 'Signing in...')
                : (isCreatingAccount ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <p className="text-center font-body text-sm text-muted-foreground mb-6">
            {isCreatingAccount ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => setIsCreatingAccount(!isCreatingAccount)}
              className="text-primary hover:opacity-80 underline transition-opacity"
            >
              {isCreatingAccount ? 'Sign in' : 'Create one'}
            </button>
          </p>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-card font-mono text-xs text-muted-foreground uppercase tracking-widest">or</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-background text-foreground border border-border rounded-sm font-body font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Continue with Google</span>
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="font-body text-xs text-muted-foreground">
            Your AI assistant is requesting access to noctua on your behalf.
          </p>
        </div>
      </div>
    </div>
  );
}
